import { Controller } from '@hotwired/stimulus';
import {
  AutoFormatOptions,
  Blocks,
  DEFAULT_TEMPLATE,
  DateParts,
  Pattern,
  defaultAutoFormatOptions,
  formatDateInput,
  getBlocks,
  parseISODate,
  setCursorPosition,
  toDefaultDateString,
  toStrippedDateString,
} from '../../utilities/date';

export default class extends Controller {
  // TESTED POSSIBILITIES: 'mm/dd/yyyy' | 'dd/mm/yyyy' | 'mm/dd' | 'dd/mm'
  static readonly targets = ['input', 'formattedInput'];
  static readonly values = {
    template: String,
  };

  declare inputTarget: HTMLInputElement;
  declare formattedInputTarget: HTMLInputElement;

  declare templateValue: string;

  private formattedDate: string = '';
  private date: DateParts = {};
  private delimiter: string = '';
  private blocks: Blocks = [];
  private pattern: Pattern = [];
  private intersectionObserver: IntersectionObserver | undefined = undefined;

  initialize(): void {
    const options: AutoFormatOptions = {
      template: this.templateValue,
      value: this.inputTarget.value,
    };
    const { pattern, value, delimiter } = defaultAutoFormatOptions(
      options,
      DEFAULT_TEMPLATE,
    );
    this.pattern = pattern;
    this.formattedDate = value;
    this.delimiter = delimiter;
    this.blocks = getBlocks(pattern);
    this.initValues();
  }

  initValues() {
    if (this.formattedInputTarget.value) {
      const parts = parseISODate(this.formattedInputTarget.value);
      const strippedValue = toStrippedDateString({
        parts,
        pattern: this.pattern,
      });
      const { formattedDate, date } = formatDateInput({
        blocks: this.blocks,
        cursorPosition: this.formattedInputTarget.value.length,
        pattern: this.pattern,
        value: strippedValue,
        prevValue: strippedValue,
        delimiter: this.delimiter,
        prevDate: this.date,
      });
      this.date = date;
      this.formattedDate = formattedDate;
    }
    this.setInputTargetValue(this.formattedDate);
  }

  connect(): void {
    this.onIntersectElement(this.element);
    this.inputTarget.addEventListener('input', this.onInput);
    this.inputTarget.addEventListener('focus', this.onFocus);
  }

  disconnect(): void {
    this.inputTarget.removeEventListener('input', this.onInput);
    this.inputTarget.removeEventListener('focus', this.onFocus);
    this.intersectionObserver?.disconnect();
  }

  private onInput = (event: Event) => {
    const target = event.target as HTMLInputElement;
    const userInput = target.value;
    const startingCursor = target.selectionStart ?? userInput.length;

    const { formattedDate, cursorPosition, date } = formatDateInput({
      blocks: this.blocks,
      cursorPosition: startingCursor,
      pattern: this.pattern,
      value: userInput,
      prevValue: this.formattedDate,
      delimiter: this.delimiter,
      prevDate: this.date,
    });

    this.formattedDate = formattedDate;
    this.date = date;
    if (startingCursor !== userInput.length) {
      setCursorPosition(target, cursorPosition);
    }
    this.setInputTargetValue(formattedDate);
    this.setFormattedInputTargetValue(date.m, date.d);
  };

  private onFocus = (event: Event) => {
    const target = event.target as HTMLInputElement;
    const userInput = target.value;
    const cursorPosition = userInput.length;
    const { formattedDate, date } = formatDateInput({
      blocks: this.blocks,
      cursorPosition,
      pattern: this.pattern,
      value: userInput,
      prevValue: this.formattedDate,
      delimiter: this.delimiter,
      prevDate: this.date,
    });
    this.formattedDate = formattedDate;
    this.date = date;
    this.setInputTargetValue(formattedDate);
    this.setFormattedInputTargetValue(date.m, date.d);
  };

  private setInputTargetValue(formattedDate: string) {
    this.inputTarget.value = formattedDate;
  }

  private setFormattedInputTargetValue(month: string, day: string) {
    this.formattedInputTarget.value = toDefaultDateString(month, day);
  }

  private onIntersectElement(node: Element) {
    this.intersectionObserver = new IntersectionObserver(this.onIntersection);
    this.intersectionObserver.observe(node);
  }

  private onIntersection = (entries: IntersectionObserverEntry[]) => {
    for (const entry of entries) {
      if (entry.isIntersecting) {
        this.initValues();
      }
    }
  };
}
